import React from 'react';

/**
 * The part of the screen the user can actually see, in layout-viewport CSS
 * pixels: `{ top, height, covered }`, or `null` where there is no
 * `visualViewport` (SSR, jsdom without a double, very old engines).
 *
 * Why not the kit's `useVisualViewportInsets` (PM-0181): that
 * hook derives the keyboard as `innerHeight − (vv.height + vv.offsetTop)`.
 * On Android Chrome `innerHeight` is the LAYOUT viewport and keeps its height
 * when the keyboard opens, so the difference is the keyboard. On iOS Safari
 * `innerHeight` follows the VISUAL viewport — it shrinks with the keyboard
 * (and with pinch-zoom) — so the same difference is ≈ 0 and a sheet built on
 * it stays under the keys on an iPhone. The box itself, `[offsetTop,
 * offsetTop + height]`, means the same thing on both engines, so a sheet that
 * positions itself BY the box needs no `innerHeight` at all.
 *
 * `covered` is how much of the layout viewport is out of view (keyboard, pan,
 * zoom), measured against `documentElement.clientHeight` — the initial
 * containing block, which neither engine resizes for the keyboard. It is only
 * a flag-grade number (is a keyboard up?), never used for geometry.
 *
 * Rounded to whole pixels so a keyboard animation frame at a sub-pixel
 * position does not re-render anything.
 */
export default function useVisibleBox() {
  const [box, setBox] = React.useState(null);

  React.useEffect(() => {
    const vv = typeof window === 'undefined' ? null : window.visualViewport;
    if (!vv) return undefined;
    const read = () => {
      const top = Math.max(0, Math.round(vv.offsetTop));
      const height = Math.round(vv.height);
      const layout = document.documentElement.clientHeight || window.innerHeight || height;
      const covered = Math.max(0, Math.round(layout - height));
      setBox((prev) => (prev && prev.top === top && prev.height === height && prev.covered === covered
        ? prev
        : { top, height, covered }));
    };
    read();
    vv.addEventListener('resize', read);
    vv.addEventListener('scroll', read);
    return () => {
      vv.removeEventListener('resize', read);
      vv.removeEventListener('scroll', read);
    };
  }, []);

  return box;
}
